import Vue from 'vue';
import { MutationTree } from 'vuex';

// Shared types
import { Accessory, FromSpacesResponse, IndoorUnit, OutdoorUnit, Map, System } from '@coldtec/cos-types';

// Store and store types
import Store from '@/store';
import {
    RootState,
    SystemsMutation,
    SystemsFilterGroup,
    SystemSplit,
    SystemsFilters,
    ToolAccessory,
    ToolSystem,
    ToolUnit,
    ToolSystemSuggestionMap,
    SystemUnitLocation,
} from '@/store/store.types';

/**
 *
 *  Used to marshal the data of units into something the tool can work with.
 *
 *  @param units The original units data to marshal
 *
 *  @returns An Array of tool units
 *
 */
function marshalUnits(units: Array<IndoorUnit> | Array<OutdoorUnit> | undefined, location: SystemUnitLocation): Array<ToolUnit> {

    const toolUnits: Array<ToolUnit> = [];

    (units || []).forEach((unit: IndoorUnit | OutdoorUnit, index: number) => {

        toolUnits.push({
            guid: unit.guid,
            location,
            localId: `${ unit.guid }-${ index }`,
            toolSpaceId: null,
            resetToolSpaceId: null,
            color: null,
            data: unit,
        });

    });

    return toolUnits;

}

/**
 *
 *  Used to marshal the data of accessories into something the tool can work with.
 *
 *  @param accessories The original accessories data to marshal
 *
 *  @returns An Array of tool accessories
 *
 */
function marshalAccessories(accessories: Array<Accessory> | undefined): Array<ToolAccessory> {

    const toolAccessories: Array<ToolAccessory> = [];

    for (const accessory of accessories || []) {

        toolAccessories.push({
            guid: accessory.guid,
            selected: false,
            data: accessory,
        });

    }

    return toolAccessories;

}

/**
 *
 *  Used to construct a tool system.
 *
 *  @param split The type of split
 *  @param guid The guid of the system
 *  @param systems A map that holds all the suggested systems
 *
 *  @returns The tool system
 *
 */
function constructToolSystem(split: SystemSplit, guid: string, systems: Map<System>): ToolSystem {

    return {
        split,
        guid,
        selected: false,
        indoorUnits: marshalUnits(systems[guid].indoor_units as Array<IndoorUnit> | undefined, SystemUnitLocation.Indoor),
        outdoorUnits: marshalUnits(systems[guid].outdoor_units as Array<OutdoorUnit> | undefined, SystemUnitLocation.Outdoor),
        accessories: marshalAccessories(systems[guid].accessories as Array<Accessory> | undefined),
    };

}

/**
 *  Holding all the mutations of the systems.
 */
export const Mutations: MutationTree<RootState> = {

    /**
     *  Used to update the loading state of the suggestions.
     */
    [SystemsMutation.LOADING](state: RootState, loading: boolean): void {

        state.systems.loading = loading;

    },

    /**
     *  Used to store the available filters and construct their states.
     */
    [SystemsMutation.FILTERS_AVAILABLE](state: RootState, filters: Array<SystemsFilterGroup>): void {

        state.systems.filters.available = filters;

        /**
         *  Construct a state per filter (value).
         */
        for (const filter of filters) {

            const values: Record<string, boolean> = {};

            for (const option of filter.options) {

                values[String(option.value)] = false;

            }

            Vue.set(state.systems.filters.state, filter.column, values);

        }

    },

    /**
     *  Used to update the state of the filters.
     */
    [SystemsMutation.FILTERS_STATE](state: RootState, filters: SystemsFilters): void {

        state.systems.filters.state = filters;

    },

    /**
     *  Used to store the fetched system suggestions and
     *  set the system's fetching state to `false`.
     */
    [SystemsMutation.SUGGESTIONS](state: RootState, suggestions: FromSpacesResponse): void {

        /**
         *
         *  Catch the space that should be opened
         *  after storing the suggestions.
         *
         *  (i) Needed to open the space to which got
         *  redirected from the overview step
         *
         */
        let showSystemSpaceId = state.systems.suggestions.show || '';

        /**
         *  Marshal the data of the single splits, so the systems step can use them.
         */
        const singleSplits: ToolSystemSuggestionMap = {};

        for (const single of suggestions.single) {

            /**
             *  Use the single space's tool id as `systemSpaceId`.
             */
            const systemSpaceId = single.tool_space_id;

            /**
             *  Catch the first systemSpaceId if not catched already.
             */
            if (!showSystemSpaceId.length) {

                showSystemSpaceId = systemSpaceId;

            }

            /**
             *  Construct the single split tool systems.
             */
            const systems: Array<ToolSystem> = [];

            for (const system of single.systems) {

                systems.push(
                    constructToolSystem(SystemSplit.Single, system, suggestions.map),
                );

            }

            /**
             *  Add the composed tool systems to the single splits.
             */
            singleSplits[systemSpaceId] = {
                toolSpaceIds: [
                    single.tool_space_id,
                ],
                systems,
            };

        }

        /**
         *  Marshal the data of the multi splits, so the systems step can use them.
         */
        const multiSplits: ToolSystemSuggestionMap = {};

        for (const multi of suggestions.multi) {

            /**
             *  Use the multi's tool space ids as systemSpaceId.
             */
            const systemSpaceId = multi.tool_space_ids.join('-');

            /**
             *  Catch the first systemSpaceId if not catched already.
             */
            if (!showSystemSpaceId.length) {

                showSystemSpaceId = systemSpaceId;

            }

            /**
             *  Construct the multi split tool systems.
             */
            const systems: Array<ToolSystem> = [];

            for (const system of multi.systems) {

                systems.push({
                    ...constructToolSystem(SystemSplit.Multi, system.system, suggestions.map),
                    layouts: system.layouts,
                });

            }

            /**
             *  Add the composed tool systems to the multi splits.
             */
            multiSplits[systemSpaceId] = {
                toolSpaceIds: multi.tool_space_ids,
                systems,
            };

        }

        /**
         *  Store the splits, first systemSpaceId and the data
         *  of all the systems that are suggested.
         */
        state.systems.suggestions = {
            single: singleSplits,
            multi: multiSplits,
            show: showSystemSpaceId,
            systems: suggestions.map,
        };

        Store.commit(SystemsMutation.LOADING, false);

    },

    /**
     *
     *  Used to store the systemSpaceId of the space for
     *  which the suggestions should be shown.
     *
     *  (i) The `SystemsSuggestions.vue` component on
     *  his turn renders the correct systems
     *
     */
    [SystemsMutation.SUGGESTIONS_SHOW_FOR_SPACE](state: RootState, systemSpaceId: string): void {

        state.systems.suggestions.show = systemSpaceId;

    },

};
